/*----------------------------------
- METRICS: THE GA CONFIG SCRIPT
----------------------------------*/

/**
 * The inline script that queues GA's config and its first page view (`Component.tsx` pushes it on `page.scripts`).
 *
 * @docs docs/features/website-seo-performance
 * @rule GA's first page view never carries `returnTo`: an invitation link signs in through
 *       `/?conversion=auth&...&returnTo=/invite/team/<token>`, and before batch 6 that first page view sent the
 *       single-use invitation token to GA (review G3F-1, batch 6 G17). Only that first page view is cleaned: the
 *       conversion modal writes `returnTo` back onto the URL while it is open (`WebsiteConversionModalService`), so GA's
 *       later hits on that address (form and scroll events, history page views) and Mixpanel still carry the token.
 *       Keeping the modal from writing it is a follow-up for the paid-only read (S9, Gaetan's letter A on review M2).
 */

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The conversion modal's return target (`packages/components/conversion/authIntent.ts`), kept out of GA's page location. */
export const GA_STRIPPED_QUERY_KEYS = ['returnTo'] as const;

/*----------------------------------
- PUBLIC API
----------------------------------*/

/**
 * The queue is set up inline and at once, so the page view keeps its real timestamp; gtag.js replays `dataLayer`
 * whenever it arrives.
 * Decision: the config sends no page view of its own and the first page view is an event carrying the cleaned
 * location. A `page_location` passed to `config` would stick to every later event of the tag, so the page views of
 * client-side navigations would all report the landing address (adversarial review of batch 6). A browser without
 * `URL` sends no first page view rather than the raw address. An address with no stripped key goes out untouched:
 * `searchParams` re-serialises the whole query (a `%20` becomes `+`), which would change what GA recorded before.
 */
export const buildGaConfigInline = (measurementId: string): string =>
    `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', ${JSON.stringify(measurementId)}, { send_page_view: false });
try {
    var udPageLocation = new URL(window.location.href);
    var udStripped = false;
    ${JSON.stringify(GA_STRIPPED_QUERY_KEYS)}.forEach(function (key) {
        if (udPageLocation.searchParams.has(key)) { udPageLocation.searchParams.delete(key); udStripped = true; }
    });
    gtag('event', 'page_view', { page_location: udStripped ? udPageLocation.href : window.location.href });
} catch (error) {}
`.trim();
